/* eslint-disable react/no-array-index-key */
/* eslint-disable react/jsx-key */
/* eslint no-underscore-dangle: ["error", { "allow": [ "_id"] }] */
import React, { useEffect, useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import { DataGrid, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
// import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  Stack,
  Typography,
  Card,
  Chip,
  CardContent,
  TextField,
  Grid,
  InputAdornment,
  CircularProgress,
  IconButton,
  Button,
  CardActions
} from '@mui/material';
import { Formik, Field } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import dateFormat from 'dateformat';
import DatePicker from 'react-datepicker';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import 'react-datepicker/dist/react-datepicker.css';
import LoadingSkeleton from '../../utils/LoadingSkeleton';
import NoRowsOverlay from '../../utils/NoRowOverlay';
import ReportAction from '../../store/actions/ReportAction';
// import MultiRangeProgressBar from '../dashboard/totalReceivables';

const printStyle = {
  '@media print': {
    '@page': {
      size: 'landscape'
    },
    '.MuiDataGrid-main': {
      overflow: 'scroll',
      width: '1100px',
      '.MuiDataGrid-root': {
        fontSize: '12px !important'
      },
      '.MuiDataGrid-columnHeader': {
        fontSize: '12px !important',
        minWidth: '150px !important',
        width: 'auto !important'
      },
      '.MuiDataGrid-cell': {
        fontSize: '12px !important',
        padding: '6px !important',
        minWidth: '150px !important',
        width: 'auto !important',
        '.MuiTypography-root': {
          fontSize: '12px !important'
        }
      }
    }
  }
};

function CustomToolbar({ hasData }) {
  return (
    <Box
      className="tableTopWrap page__title"
      sx={{
        borderBottom: '1px solid rgba(0,0,0,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        p: 3
      }}
    >
      <GridToolbarContainer>
        {hasData && (
          <GridToolbarExport
            csvOptions={{
              fileName: 'ageing-report',
              utf8WithBom: true
            }}
            printOptions={{
              disableToolbarButton: true
              // allColumns: true,
              // hideFooter: true,
              // hideToolbar: true
            }}
            variant="contained"
            size="small"
          />
        )}
      </GridToolbarContainer>
    </Box>
  );
}

CustomToolbar.propTypes = {
  hasData: PropTypes.bool.isRequired
};

export default function AgeingReport() {
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });

  const [sortInfo, setSortInfo] = useState({
    sort_by: null,
    order_by: null
  });

  // const navigate = useNavigate();
  const [rowCountState, setRowCountState] = useState(0);
  const [data, setData] = useState([]);
  // const [updatedValue, setUpdatedValue] = useState({});
  const [currentFilters, setCurrentFilters] = useState({
    client: [],
    department: [],
    invoice: [],
    status: [],
    currency: []
  });
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const dispatch = useDispatch();

  const { ageingData, isLoading, ageingFilterData } = useSelector((state) => state.report);

  const { clients, currency, departments, status, invoiceNumbers } = ageingFilterData;

  const initialValues = {
    client: [],
    department: [],
    invoice: [],
    status: [],
    to_date: null,
    from_date: null,
    currency: []
  };

  useEffect(() => {
    dispatch(ReportAction.getAgeingFilters());
  }, [dispatch]);

  useEffect(() => {
    if (ageingData) {
      if (ageingData.data) {
        setData(ageingData.data);
      } else {
        setData(null);
      }
      setRowCountState((prevRowCountState) =>
        ageingData.total !== undefined ? ageingData.total : prevRowCountState
      );
    }
  }, [ageingData]);

  const columns = [
    {
      field: 'name',
      headerName: 'Client Name',
      flex: 0.75
    },
    {
      field: 'invoice_number',
      headerName: 'Invoice No',
      flex: 0.75
    },
    {
      field: 'departments',
      headerName: 'Departments',
      sortable: false,
      flex: 1,
      valueGetter: (params) => params.value.map((dept) => dept.name).join(', ')
    },
    {
      field: 'invoice_date',
      headerName: 'Invoice Date',
      flex: 1,
      renderCell: (params) => {
        if (params && params.value) {
          return (
            <Typography component="p" variant="body1">
              {params.value}
            </Typography>
          );
        }
        return (
          <Typography component="p" variant="body1">
            -
          </Typography>
        );
      }
    },
    {
      field: 'grand_total',
      headerName: 'Total Amount',
      flex: 0.75,
      valueGetter: (params) => {
        if (params && params.value && params.row) {
          return `${params.row.short_name} ${params.value}`;
        }
        return null;
      }
    },
    {
      field: 'ageing_days',
      headerName: 'Ageing Day(s)',
      flex: 1.25,
      valueGetter: (params) => {
        return `${params.row.ageing_days} Day(s)`;
      }
    },
    {
      field: 'status_name',
      headerName: 'Payment Status',
      flex: 0.75,
      renderCell: (params) => {
        if (params && params.value) {
          return (
            <Chip label={params.value} color={params.value.toLowerCase().replace(/\s+/g, '')} />
          );
        }
        return null;
      }
    }
  ];

  // Unified function to build payload
  const buildPayload = useCallback(
    (
      filters = currentFilters,
      overridePagination = null,
      overrideSort = null,
      overrideDateRange = null
    ) => {
      const effectivePagination = overridePagination || paginationModel;
      const effectiveSort = overrideSort || sortInfo;
      const effectiveDateRange = overrideDateRange || dateRange;

      const payload = {
        ...filters,
        ...effectivePagination,
        ...effectiveSort,
        page: (effectivePagination.page ?? 0) + 1, // Backend expects 1-based index
        from_date: effectiveDateRange[0] ? dateFormat(effectiveDateRange[0], 'yyyy-mm-dd') : null,
        to_date: effectiveDateRange[1] ? dateFormat(effectiveDateRange[1], 'yyyy-mm-dd') : null
      };

      // Transform array values into IDs
      if (payload.client?.length) payload.client = payload.client.map((item) => item._id);
      if (payload.department?.length)
        payload.department = payload.department.map((item) => item._id);
      if (payload.invoice?.length) payload.invoice = payload.invoice.map((item) => item._id);
      if (payload.status?.length) payload.status = payload.status.map((item) => item.id);
      if (payload.currency?.length) payload.currency = payload.currency.map((item) => item._id);

      return payload;
    },
    [currentFilters, dateRange, paginationModel, sortInfo]
  );

  // Unified filter update function
  const updateFilters = useCallback(
    /* eslint-disable default-param-last */
    (
      overrideFilters = null,
      setErrors,
      setSubmitting,
      overridePagination = null,
      overrideSort = null,
      overrideDateRange = null
    ) => {
      const filtersToUse = overrideFilters
        ? { ...currentFilters, ...overrideFilters }
        : currentFilters;

      if (overrideFilters) {
        setCurrentFilters(filtersToUse);
      }
      const payload = buildPayload(
        filtersToUse,
        overridePagination,
        overrideSort,
        overrideDateRange
      );
      dispatch(ReportAction.getAgeingReport(payload, setErrors, setSubmitting));
    },
    [dispatch, buildPayload, currentFilters]
  );

  // Handle date range changes
  const handleRangePicker = useCallback(
    (date) => {
      if (!date || date.length !== 2 || date[1] === null) return;
      setDateRange(date);
      updateFilters(null, undefined, undefined, null, null, date);
    },
    [updateFilters]
  );

  // Handle sorting changes
  const handleSortModelChange = useCallback(
    (sortModel) => {
      if (sortModel.length > 0) {
        const { field, sort } = sortModel[0];
        const sortData = { sort_by: field, order_by: sort };
        setSortInfo(sortData);
        updateFilters(null, undefined, undefined, null, sortData);
      }
    },
    [updateFilters]
  );

  // Handle pagination changes
  const handlePageModelChange = useCallback(
    (pageOption) => {
      setPaginationModel(pageOption);
      updateFilters(null, undefined, undefined, pageOption);
    },
    [updateFilters]
  );

  return (
    <Box sx={{ mb: 3, minHeight: '50vh' }} className="cardWrapper">
      <div className="pageTitleWrap">
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography component="h2" variant="h2">
            Ageing Report
          </Typography>
        </Stack>
      </div>
      <Card className="pageTitleWrap" sx={{ mb: 3 }}>
        <Formik
          initialValues={initialValues}
          onSubmit={(values, { setErrors, setSubmitting }) => {
            updateFilters(values, setErrors, setSubmitting);
            // const intialPageModal = {
            //   page: 1,
            //   pageSize: 10
            // };
            // setPaginationModel(intialPageModal);
            // const newValues = { ...values, ...paginationModel, ...sortInfo };
            // const newClientId = newValues.client.map((item) => item._id);
            // newValues.client = newClientId;
            // const newPage = newValues.page + 1;
            // newValues.page = newPage;
            // const newDepartId = newValues.department.map((item) => item._id);
            // newValues.department = newDepartId;
            // const newInoviceId = newValues.invoice.map((item) => item._id);
            // newValues.invoice = newInoviceId;
            // const newStatusId = newValues.status.map((item) => item.id);
            // newValues.status = newStatusId;

            // const newCurrencyId = newValues.currency.map((item) => item._id);
            // newValues.currency = newCurrencyId;
            // newValues.to_date = endDate;
            // newValues.from_date = startDate;
            // setUpdatedValue(newValues);
            // dispatch(ReportAction.getAgeingReport(newValues, setErrors, setSubmitting));
          }}
        >
          {({
            // errors,
            // touched,
            values,
            // handleBlur,
            // handleChange,
            handleSubmit,
            isSubmitting,
            handleReset,
            setFieldValue
          }) => (
            <form onSubmit={handleSubmit} autoComplete="off">
              <CardContent>
                <Grid container columnSpacing={3} rowSpacing={2}>
                  <Grid item md={3} lg={3}>
                    <Field
                      className="filter"
                      name="client"
                      value={values.client}
                      component={Autocomplete}
                      fullWidth
                      multiple
                      size="small"
                      options={clients ?? []}
                      onChange={(e, newvalue) => {
                        if (newvalue) {
                          setFieldValue('client', newvalue);
                        } else {
                          setFieldValue('client', null);
                        }
                      }}
                      sx={{ mt: 0 }}
                      getOptionLabel={(option) => option.name}
                      renderOption={(props, option) => {
                        return (
                          <li {...props} key={option._id}>
                            {option.name}
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Client"
                          placeholder="Search Client"
                          variant="outlined"
                          margin="none"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item md={3} lg={3}>
                    <Field
                      name="department"
                      component={Autocomplete}
                      value={values.department}
                      className="filter"
                      fullWidth
                      multiple
                      size="small"
                      options={departments ?? []}
                      onChange={(e, newvalue) => {
                        if (newvalue) {
                          setFieldValue('department', newvalue);
                        } else {
                          setFieldValue('department', null);
                        }
                      }}
                      sx={{ mt: 0 }}
                      getOptionLabel={(option) => option.name}
                      renderOption={(props, option) => {
                        return (
                          <li {...props} key={option._id}>
                            {option.name}
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Department"
                          placeholder="Search Departments"
                          variant="outlined"
                          margin="none"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item md={3} lg={3}>
                    <Box className="datepicker">
                      <DatePicker
                        selectsRange
                        startDate={startDate}
                        endDate={endDate}
                        popperPlacement="bottom"
                        onChange={(update) => {
                          setDateRange(update);
                          handleRangePicker(update);
                        }}
                        monthsShown={2}
                        isClearable
                        customInput={
                          <TextField
                            label="Date"
                            fullWidth
                            size="small"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="start">
                                  <IconButton
                                    aria-label="toggle password visibility"
                                    edge="end"
                                    disabled
                                  >
                                    <CalendarMonthIcon sx={{ color: '#fff' }} />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                          />
                        }
                      />
                    </Box>
                  </Grid>
                  <Grid item md={3} lg={3}>
                    <Field
                      component={Autocomplete}
                      className="filter"
                      name="invoice"
                      value={values.invoice}
                      fullWidth
                      multiple
                      size="small"
                      options={invoiceNumbers ?? []}
                      onChange={(e, newvalue) => {
                        if (newvalue) {
                          setFieldValue('invoice', newvalue);
                        } else {
                          setFieldValue('invoice', null);
                        }
                      }}
                      sx={{ mt: 0 }}
                      getOptionLabel={(option) => option.invoice_number}
                      renderOption={(props, option) => {
                        return (
                          <li {...props} key={option._id}>
                            {option.invoice_number}
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="invoice"
                          label="Invoice Number"
                          placeholder="Search Invoice Number"
                          variant="outlined"
                          margin="none"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item md={3} lg={3}>
                    <Field
                      className="status"
                      name="status"
                      component={Autocomplete}
                      fullWidth
                      multiple
                      value={values.status}
                      size="small"
                      options={status ?? []}
                      onChange={(e, newvalue) => {
                        if (newvalue) {
                          setFieldValue('status', newvalue);
                        } else {
                          setFieldValue('status', null);
                        }
                      }}
                      sx={{ mt: 0 }}
                      getOptionLabel={(option) => option.status}
                      renderOption={(props, option) => {
                        return (
                          <li {...props} key={option.id}>
                            {option.status}
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Payment Status"
                          placeholder="Search Status"
                          variant="outlined"
                          margin="none"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item md={3} lg={3}>
                    <Field
                      className="currency"
                      name="currency"
                      component={Autocomplete}
                      fullWidth
                      size="small"
                      value={values.currency}
                      multiple
                      options={currency ?? []}
                      onChange={(e, newvalue) => {
                        if (newvalue) {
                          setFieldValue('currency', newvalue);
                        } else {
                          setFieldValue('currency', null);
                        }
                      }}
                      sx={{ mt: 0 }}
                      getOptionLabel={(option) => option.name}
                      renderOption={(props, option) => {
                        return (
                          <li {...props} key={option._id}>
                            {option.name}
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Currency"
                          placeholder="Search Currency"
                          variant="outlined"
                          margin="none"
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', p: 3 }}>
                <Button
                  type="submit"
                  className="save__btn"
                  variant="contained"
                  color="secondary"
                  // fullWidth
                  disabled={isSubmitting}
                  size="medium"
                >
                  {isSubmitting ? <CircularProgress size={20} /> : 'Get Report'}
                </Button>
                <Button
                  type="button"
                  className="cancel__btn"
                  variant="contained"
                  // fullWidth
                  color="info"
                  size="medium"
                  onClick={() => {
                    handleReset();
                    setData(null);
                    setDateRange([null, null]);
                  }}
                >
                  Clear
                </Button>
              </CardActions>
            </form>
          )}
        </Formik>
      </Card>

      {data && (
        <Box sx={{ bgcolor: 'white' }} className="dataTable__wrap">
          <DataGrid
            rows={data}
            getRowId={(row) => row._id}
            rowCount={rowCountState}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 10
                }
              }
            }}
            autoHeight
            sx={{
              minHeight: 650,
              '--DataGrid-overlayHeight': '50vh',
              ...printStyle
            }}
            disableColumnFilter
            rowHeight={50}
            slots={{
              toolbar: CustomToolbar,
              loadingOverlay: LoadingSkeleton,
              noRowsOverlay: NoRowsOverlay
            }}
            slotProps={{
              toolbar: {
                hasData: data.length > 0,
                exportButton: {
                  csvOptions: {
                    fieldSeparator: ',',
                    fields: columns.map((column) => ({
                      label: column.headerName,
                      value: column.valueGetter
                        ? (params) => column.valueGetter(params)
                        : (params) => params.value
                    }))
                  }
                }
              }
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            paginationModel={paginationModel}
            paginationMode="server"
            onPaginationModelChange={handlePageModelChange}
            sortingMode="server"
            onSortModelChange={handleSortModelChange}
            disableRowSelectionOnClick
            loading={isLoading}
          />
        </Box>
      )}
      {/* <MultiRangeProgressBar data={totalReceivables} currency={currencyName} /> */}
    </Box>
  );
}
